/**
 * Solid layout controller for the VSCode shell (ADR-0075): panel sizes,
 * collapse flags, and the active sidebar view, hydrated from / persisted to
 * `localStorage` via the pure {@link ../glue/layout-store.ts | layout-store}.
 *
 * Persistence is explicit (on resize-commit and on toggles), not on every drag
 * tick — `onInput` only moves the signal; `onCommit`/toggles call `persist()`.
 */
import { createSignal } from 'solid-js';
import { browserLocalStorage } from '../glue/browser-storage.ts';
import {
  LAYOUT_BOUNDS,
  LAYOUT_DEFAULTS,
  type LayoutState,
  type SidebarView,
  type StorageLike,
  loadLayout,
  saveLayout,
} from '../glue/layout-store.ts';

export interface LayoutController {
  sidebarW(): number;
  consoleH(): number;
  previewW(): number;
  sidebarCollapsed(): boolean;
  consoleCollapsed(): boolean;
  view(): SidebarView;
  setSidebarW(px: number): void;
  setConsoleH(px: number): void;
  setPreviewW(px: number): void;
  setView(v: SidebarView): void;
  toggleSidebar(): void;
  /** Switch to `v`; if already showing `v`, collapse the sidebar (VSCode behaviour). */
  selectView(v: SidebarView): void;
  toggleConsole(): void;
  resetSidebarW(): void;
  resetConsoleH(): void;
  resetPreviewW(): void;
  /** Snapshot the current state into `localStorage`. */
  persist(): void;
  readonly bounds: typeof LAYOUT_BOUNDS;
}

export function useLayout(): LayoutController {
  const storage: StorageLike | undefined = browserLocalStorage();
  const initial = loadLayout(storage);

  const [sidebarW, setSidebarW] = createSignal(initial.sidebarW);
  const [consoleH, setConsoleH] = createSignal(initial.consoleH);
  const [previewW, setPreviewW] = createSignal(initial.previewW);
  const [sidebarCollapsed, setSidebarCollapsed] = createSignal(initial.sidebarCollapsed);
  const [consoleCollapsed, setConsoleCollapsed] = createSignal(initial.consoleCollapsed);
  const [view, setView] = createSignal<SidebarView>(initial.view);

  const snapshot = (): LayoutState => ({
    sidebarW: sidebarW(),
    consoleH: consoleH(),
    previewW: previewW(),
    sidebarCollapsed: sidebarCollapsed(),
    consoleCollapsed: consoleCollapsed(),
    view: view(),
  });
  const persist = (): void => saveLayout(storage, snapshot());

  return {
    sidebarW,
    consoleH,
    previewW,
    sidebarCollapsed,
    consoleCollapsed,
    view,
    setSidebarW,
    setConsoleH,
    setPreviewW,
    setView(v) {
      setView(v);
      persist();
    },
    toggleSidebar() {
      setSidebarCollapsed((c) => !c);
      persist();
    },
    selectView(v) {
      if (view() === v && !sidebarCollapsed()) {
        setSidebarCollapsed(true);
      } else {
        setView(v);
        setSidebarCollapsed(false);
      }
      persist();
    },
    toggleConsole() {
      setConsoleCollapsed((c) => !c);
      persist();
    },
    resetSidebarW() {
      setSidebarW(LAYOUT_DEFAULTS.sidebarW);
      persist();
    },
    resetConsoleH() {
      setConsoleH(LAYOUT_DEFAULTS.consoleH);
      persist();
    },
    resetPreviewW() {
      setPreviewW(LAYOUT_DEFAULTS.previewW);
      persist();
    },
    persist,
    bounds: LAYOUT_BOUNDS,
  };
}
